import type { KeyboardEvent, SyntheticEvent } from 'react';
import { isActivationKey, toLinkText, writeLinkClickAttrs, type LinkClickAttrs } from 'src/utils/link-click';

/**
 * WAT-12478 — press-time link name: for any click target whose accessible name
 * must differ from its analytics name — a tab or chip with price / channel
 * text, a card, a widget container, a button whose label is rich content.
 *
 * GTM names the click from aria-label; we always supply it — statically, or at
 * press time where a static aria-label would replace a richer accessible name.
 * Whether GTM falls back to visible text is unconfirmed, so nothing relies on it.
 *
 * Here the name is the press-time kind:
 *   - `data-linkposition` / `data-linkdestination` are rendered as explicit props;
 *   - the analytics `aria-label` is written on pointer-down (and, where the
 *     caller wires it, Enter / Space), which always precede the click GTM reads;
 *   - the element's own name (its previous `aria-label`, or none) is put back
 *     only after the element's OWN click (`setTimeout(0)` after it). A touch
 *     tap's click arrives in a later task than pointerup, so pointerup / keyup
 *     only arm a long fallback for a press that never produces a click;
 *   - an abandoned press (pointercancel, the pointer leaving with a button
 *     down, focus leaving) restores at once.
 *
 * Not a hook: safe in loops and conditionals. Attribute names are literals only
 * (`writeLinkClickAttrs`), never spread.
 */
const originalNames = new WeakMap<Element, string | null>();

/** How long after pointerup / keyup a press that produced no click keeps the analytics name. */
export const PRESS_RESTORE_FALLBACK_MS = 750;

function restoreName(el: Element): void {
  if (!originalNames.has(el)) {
    return;
  }
  const original = originalNames.get(el);
  originalNames.delete(el);
  if (original === null || original === undefined) {
    el.removeAttribute('aria-label');
  } else {
    el.setAttribute('aria-label', original);
  }
}

function watchForRelease(el: Element): void {
  let fallback: ReturnType<typeof setTimeout> | undefined;
  const restoreNow = () => {
    cleanup();
    restoreName(el);
  };
  // The element's own click: GTM reads the name during this dispatch; restore after it.
  const restoreAfterClick = () => {
    cleanup();
    setTimeout(() => restoreName(el), 0);
  };
  // Release only arms a fallback — on touch the click lands in a later task than pointerup.
  const armFallback = () => {
    clearTimeout(fallback);
    fallback = setTimeout(restoreNow, PRESS_RESTORE_FALLBACK_MS);
  };
  const onLeave = (e: Event) => {
    if ((e as PointerEvent)?.buttons) {
      restoreNow();
    }
  };
  function cleanup() {
    clearTimeout(fallback);
    window.removeEventListener('pointerup', armFallback, true);
    window.removeEventListener('keyup', armFallback, true);
    window.removeEventListener('pointercancel', restoreNow, true);
    el.removeEventListener('click', restoreAfterClick);
    el.removeEventListener('pointerleave', onLeave);
    el.removeEventListener('blur', restoreNow);
  }
  window.addEventListener('pointerup', armFallback, true);
  window.addEventListener('keyup', armFallback, true);
  window.addEventListener('pointercancel', restoreNow, true);
  el.addEventListener('click', restoreAfterClick);
  el.addEventListener('pointerleave', onLeave);
  el.addEventListener('blur', restoreNow);
}

/**
 * Writes `attrs` (from getLinkClickAttrs) onto `el` for one press and schedules
 * the restore of its own accessible name. No-op without an analytics name.
 */
export function writeLinkNameForPress(el: Element | null | undefined, attrs: LinkClickAttrs | null | undefined): void {
  if (!el || !attrs?.['aria-label']) {
    return;
  }
  if (!originalNames.has(el)) {
    originalNames.set(el, el.getAttribute('aria-label'));
    watchForRelease(el);
  }
  writeLinkClickAttrs(el, attrs);
}

/**
 * React handlers for the pattern above: the name is written on the element the
 * handlers are on. Spread only `onPointerDownCapture` on a focusable control —
 * writing on Enter / Space would change the focused element's accessible name
 * mid-activation; wire `onKeyDownCapture` only on a non-focused container.
 */
export function linkNameOnPress(attrs: LinkClickAttrs | null | undefined) {
  const press = (e: SyntheticEvent) => writeLinkNameForPress(e?.currentTarget as Element, attrs);
  return {
    onPointerDownCapture: press,
    onKeyDownCapture: (e: KeyboardEvent) => {
      if (isActivationKey(e)) {
        press(e);
      }
    }
  };
}

/**
 * For linkAttrsOnPress resolvers over buttons a package renders itself (Fusion ModalMedia):
 * true when `pressed` is the control for `label` — matched by its visible text, its
 * aria-label or its id, each compared as link text (trimmed, case-insensitive), so it
 * doesn't rely on the package deriving the id from the label.
 */
export function isControlFor(pressed: Element | null | undefined, label?: string | null): boolean {
  const want = toLinkText(label);
  if (!pressed || !want) {
    return false;
  }
  return [pressed.textContent, pressed.getAttribute('aria-label'), pressed.id].some(value => toLinkText(value) === want);
}

/**
 * A modal's close control: named "close" (aria-label / text / title), or with "close" as a
 * whole -/_/space-separated token of its id ("modal-close-button", not "closed-captions").
 */
export function isCloseControl(pressed: Element | null | undefined): boolean {
  if (!pressed) {
    return false;
  }
  return isControlFor(pressed, 'close') ||
    toLinkText(pressed.getAttribute('title')) === 'close' ||
    /(^|[-_\s])close($|[-_\s])/i.test(pressed.id || '');
}
